import { Badge, Button, Callout, Card, ConfirmDialog, useToast } from '@apovisio/ui';
import { hostEventSchema, waitingRoomSchema } from '@apovisio/contracts';
import type { HostEvent, WaitingRoom } from '@apovisio/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserCheck, UserX, UsersRound } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiClientError, apiFetch } from '../../lib/api';
import { useEventStream } from '../../lib/event-stream';
import { meetingKeys } from './api';

const timeFormat = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' });

/** File d'attente temps réel (SSE) ; tant que le flux est ouvert, l'hôte est signalé présent. */
export function useWaitingRoom(meetingId: string) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [room, setRoom] = useState<WaitingRoom | null>(null);
  const known = useRef<Set<string> | null>(null);
  const lastMeeting = useRef<string | null>(null);

  const onEvent = useCallback(
    (event: HostEvent) => {
      if (event.type !== 'waiting-room') return;
      const ids = new Set(event.room.waiting.map((entry) => entry.requestId));
      // Annonce polie des nouvelles arrivées (hors premier chargement).
      if (known.current) {
        for (const entry of event.room.waiting) {
          if (!known.current.has(entry.requestId)) {
            toast.show({ tone: 'info', title: `${entry.displayName} demande à rejoindre` });
          }
        }
      }
      known.current = ids;
      // Fin pour tous ou transfert de propriété : le détail de la réunion est relu.
      const meetingState = JSON.stringify(event.room.meeting);
      if (lastMeeting.current !== null && lastMeeting.current !== meetingState) {
        void queryClient.invalidateQueries({ queryKey: meetingKeys.detail(meetingId) });
      }
      lastMeeting.current = meetingState;
      setRoom(event.room);
    },
    [toast, queryClient, meetingId],
  );
  const stream = useEventStream(
    `/api/meetings/${meetingId}/events`,
    (data) => hostEventSchema.parse(data),
    onEvent,
  );

  useEffect(() => {
    const count = room?.waiting.length ?? 0;
    const base = document.title.replace(/^\(\d+\) /, '');
    document.title = count > 0 ? `(${String(count)}) ${base}` : base;
  }, [room]);

  const onError = (error: Error) => {
    toast.show({
      tone: 'danger',
      title: 'Action impossible',
      description: error instanceof ApiClientError ? error.message : undefined,
    });
  };
  const decide = useMutation({
    mutationFn: ({
      requestId,
      action,
    }: {
      requestId: string;
      action: 'admit' | 'deny' | 'remove';
    }) =>
      apiFetch(`/api/meetings/${meetingId}/admission-requests/${requestId}/${action}`, {
        method: 'POST',
        schema: waitingRoomSchema,
      }),
    onSuccess: setRoom,
    onError,
  });
  const admitAll = useMutation({
    mutationFn: () =>
      apiFetch(`/api/meetings/${meetingId}/admission-requests/admit-all`, {
        method: 'POST',
        schema: waitingRoomSchema,
      }),
    onSuccess: setRoom,
    onError,
  });
  const revoke = useMutation({
    mutationFn: (invitationId: string) =>
      apiFetch(`/api/meetings/${meetingId}/invitations/${invitationId}/revoke`, { method: 'POST' }),
    onSuccess: () => {
      toast.show({ tone: 'success', title: 'Lien révoqué : la personne a été retirée.' });
    },
    onError,
  });

  return { room, stream, decide, admitAll, revoke };
}

export type WaitingRoomState = ReturnType<typeof useWaitingRoom>;

/** Liste des personnes en attente et des invités admis, avec les décisions. */
export function WaitingRoomList({ room, stream, decide, admitAll, revoke }: WaitingRoomState) {
  const waiting = room?.waiting ?? [];
  const [confirm, setConfirm] = useState<{
    kind: 'remove' | 'revoke';
    requestId: string;
    invitationId: string;
    name: string;
  } | null>(null);
  return (
    <div className="av-stack">
      <div role="status" className="av-text-sm">
        {stream.status === 'open' ? (
          <span className="av-text-secondary">
            Vous êtes connecté : les invités voient que l’organisateur est présent.
          </span>
        ) : stream.status === 'failed' ? (
          <Callout tone="danger" title="File d’attente déconnectée.">
            <Button variant="secondary" onClick={stream.retry}>
              Réessayer
            </Button>
          </Callout>
        ) : stream.status === 'reconnecting' ? (
          <Callout tone="warning" title="Connexion perdue. Reconnexion en cours…" />
        ) : (
          <span className="av-text-secondary">Connexion à la file d’attente…</span>
        )}
      </div>
      {room && waiting.length === 0 ? (
        <p className="av-text-secondary">Personne n’attend pour le moment.</p>
      ) : null}
      {waiting.length > 1 ? (
        <div>
          <Button
            variant="secondary"
            icon={<UsersRound />}
            loading={admitAll.isPending}
            onClick={() => {
              admitAll.mutate();
            }}
          >
            Admettre tous ({waiting.length})
          </Button>
        </div>
      ) : null}
      {waiting.length > 0 ? (
        <ul className="app-list" aria-label="Personnes en attente">
          {waiting.map((entry) => (
            <li key={entry.requestId}>
              <div className="av-stack av-stack--sm">
                <strong>{entry.displayName}</strong>
                <span className="av-text-secondary av-text-sm">
                  Invitation « {entry.invitationLabel} » · depuis{' '}
                  {timeFormat.format(new Date(entry.requestedAt))}
                </span>
              </div>
              <div className="av-row">
                <Button
                  variant="secondary"
                  icon={<UserX />}
                  aria-label={`Refuser ${entry.displayName}`}
                  disabled={decide.isPending}
                  onClick={() => {
                    decide.mutate({ requestId: entry.requestId, action: 'deny' });
                  }}
                >
                  Refuser
                </Button>
                <Button
                  icon={<UserCheck />}
                  aria-label={`Admettre ${entry.displayName}`}
                  loading={decide.isPending && decide.variables.requestId === entry.requestId}
                  disabled={decide.isPending}
                  onClick={() => {
                    decide.mutate({ requestId: entry.requestId, action: 'admit' });
                  }}
                >
                  Admettre
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
      {room && room.admitted.length > 0 ? (
        <div className="av-stack av-stack--sm">
          <h3 className="av-text-sm">Admis ({room.admitted.length})</h3>
          <ul className="app-list">
            {room.admitted.map((entry) => (
              <li key={entry.requestId}>
                <span>
                  {entry.displayName}{' '}
                  <span className="av-text-secondary av-text-sm">« {entry.invitationLabel} »</span>
                </span>
                <div className="av-row">
                  <Badge tone="success">Admis</Badge>
                  <Button
                    variant="ghost"
                    aria-label={`Retirer ${entry.displayName} de la visio`}
                    onClick={() => {
                      setConfirm({
                        kind: 'remove',
                        requestId: entry.requestId,
                        invitationId: entry.invitationId,
                        name: entry.displayName,
                      });
                    }}
                  >
                    Retirer
                  </Button>
                  <Button
                    variant="ghost"
                    aria-label={`Révoquer le lien de ${entry.displayName}`}
                    onClick={() => {
                      setConfirm({
                        kind: 'revoke',
                        requestId: entry.requestId,
                        invitationId: entry.invitationId,
                        name: entry.displayName,
                      });
                    }}
                  >
                    Révoquer
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <ConfirmDialog
        open={confirm !== null}
        title={
          confirm?.kind === 'revoke'
            ? `Révoquer le lien de ${confirm.name} ?`
            : `Retirer ${confirm?.name ?? ''} de la visio ?`
        }
        description={
          confirm?.kind === 'revoke'
            ? 'La personne est retirée immédiatement et son lien cesse de fonctionner : elle ne pourra pas revenir.'
            : 'La personne est retirée de la salle. Son lien reste valable : elle pourra redemander à entrer.'
        }
        confirmLabel={confirm?.kind === 'revoke' ? 'Révoquer' : 'Retirer'}
        busy={decide.isPending || revoke.isPending}
        onCancel={() => {
          setConfirm(null);
        }}
        onConfirm={() => {
          if (!confirm) return;
          const done = {
            onSettled: () => {
              setConfirm(null);
            },
          };
          if (confirm.kind === 'revoke') revoke.mutate(confirm.invitationId, done);
          else decide.mutate({ requestId: confirm.requestId, action: 'remove' }, done);
        }}
      />
    </div>
  );
}

/** Carte « Salle d'attente » de l'écran de détail. */
export function WaitingRoomCard({ meetingId }: { meetingId: string }) {
  const waitingRoom = useWaitingRoom(meetingId);
  const count = waitingRoom.room?.waiting.length ?? 0;
  return (
    <Card
      title={count > 0 ? `Salle d’attente (${String(count)})` : 'Salle d’attente'}
      description="Les invités n’entrent qu’après votre validation."
    >
      <WaitingRoomList {...waitingRoom} />
    </Card>
  );
}
